// Dashboard — the main authenticated landing page.
// Bento-grid layout with:
//   - A cognitive-fingerprint orb at the centre (particle animation).
//   - Ring-based profile card (dimensions mapped to diagnostic results).
//   - Streak, quick actions, continue-learning, mastery, journey tiles.
//   - Real data wired from profile, content, learning-paths, recalibration
//     and user-subscription services.
//   - Rotating sayings with a per-user customisation modal (Firestore).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import {
  Flame,
  Upload,
  Award,
  Sparkles,
  ArrowRight,
  Zap,
  Crown,
  Users,
  Lock,
  Pencil,
  X,
  Fingerprint,
  SlidersHorizontal,
} from 'lucide-react';
import { useAuthStore, useRecalibrationStore } from '@shared/state';
import { useTutorStore } from '@shared/state/tutor-store';
import {
  ensureRecalibrationSchedule,
  subscribeToRecalibrationSchedule,
  subscribeToDay14Offer,
} from '@shared/services/recalibration-api';
import {
  loadCustomSayings,
  saveCustomSayings,
  subscribeToCustomSayings,
} from '@shared/services/customSayings';
import { loadProfile } from '@shared/services/profile';
import { loadUserContent } from '@shared/services/content';
import { getUserLearningPaths } from '@shared/services/learningPath';
import { trackPageView } from '@shared/services/analytics';
import { TIER_LIMITS } from '@shared/constants';
import type { Day14Offer, RecalibrationStatus } from '@shared/types';
import type { CogniPrintProfile } from '@shared/types/cogniprint';
import type { ContentDocument } from '@shared/types/content';
import type { LearningPath } from '@shared/types/learning-path';
import type { SubscriptionTier } from '@shared/constants/tiers';
import type { UserProfile } from '@shared/types/user';
import { THICK_TILE_STYLE, THICK_TILE_CLASSNAME } from '../components/dashboard/tileStyles';
import { NotchedTile, ORB_RADIUS } from '../components/dashboard/NotchedTile';
import { FingerprintRings } from '../components/dashboard/FingerprintRings';
import { TutorEntryTile } from '../components/dashboard/TutorEntryTile';
import { ParticleFingerprint } from '../components/dashboard/ParticleFingerprint';
import { getDimensionsFromProfile } from '../components/dashboard/dimensionHelpers';
import type { DimensionDisplayData } from '../components/dashboard/dimensionHelpers';
import { ProfileRevealScreen, ProfileExpandedOverlay } from '../components/dashboard/ProfileReveal';
import type { CardFlightData } from '../components/dashboard/ProfileReveal';
import { useCollapseTransition } from '../components/dashboard/CollapseTransition';
import { ContinueLearningBanner } from '../components/dashboard/ContinueLearningBanner';
import { LearningPathsList } from '../components/dashboard/LearningPathsList';
import { MasteryDashboard } from '../components/dashboard/MasteryDashboard';
import { YourJourney } from '../components/dashboard/YourJourney';
import { ReadinessBanner } from '../components/dashboard/ReadinessBanner';
import { DueForReviewCard } from '../components/dashboard/DueForReviewCard';
import { SystemHealthBadge } from '../components/dashboard/SystemHealthBadge';
import { UnacknowledgedChangesBanner } from '../components/dashboard/UnacknowledgedChangesBanner';
import { RecalibrationCountdown } from '../components/dashboard/RecalibrationCountdown';
import { PreviewModal } from '../components/recalibration/PreviewModal';
import { CheckWizard } from '../components/recalibration/CheckWizard';
import { FullWizard } from '../components/recalibration/FullWizard';
import { Day14CtaBanner } from '../components/recalibration/Day14CtaBanner';
import { AutoPilotCard } from '../components/recalibration/AutoPilotCard';
import { TutorPanel } from '../components/tutor/TutorPanel';
import { Button } from '../components/ui/Button';
import { DimensionMeter } from '../components/profile/DimensionMeter';

const REVEAL_SEEN_KEY = 'cogniprint_hasSeenProfileReveal';

// ─────────────────────────────────────────────────────────────
// Greeting helpers
// ─────────────────────────────────────────────────────────────

/** "3d" / "5w" / "—" — how long ago the profile was last recalibrated. */
function relativeAge(iso: string | undefined | null): string {
  if (!iso) return '—';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '—';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'today';
  if (days < 7) return `${days}d`;
  if (days < 60) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// ─────────────────────────────────────────────────────────────
// Dashboard — main authenticated landing page
// ─────────────────────────────────────────────────────────────

export function Dashboard() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const enterAdaptationMode = useTutorStore((s) => s.enterAdaptationMode);
  const setSchedule = useRecalibrationStore((s) => s.setSchedule);
  const recalStatus = useRecalibrationStore((s) => s.status);

  // Data state
  const [profile, setProfile] = useState<CogniPrintProfile | null>(null);
  const [content, setContent] = useState<ContentDocument[]>([]);
  const [learningPaths, setLearningPaths] = useState<LearningPath[]>([]);
  const [loading, setLoading] = useState(true);
  const [day14Offer, setDay14Offer] = useState<Day14Offer | null>(null);

  // Modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);
  const [fullWizardOpen, setFullWizardOpen] = useState(false);

  // Profile-reveal flow state — 'reveal' plays once after first diagnostic,
  // then transitions via 'collapsing' to the regular 'dashboard' view.
  const [view, setView] = useState<'reveal' | 'collapsing' | 'dashboard'>('dashboard');
  const [profileExpanded, setProfileExpanded] = useState(false);
  const profileTargetRef = useRef<HTMLDivElement>(null);

  const onTransitionComplete = useCallback(() => {
    setView('dashboard');
    try { localStorage.setItem(REVEAL_SEEN_KEY, 'true'); } catch { /* ignore */ }
  }, []);

  const { collapsePhase, particleTarget, startCollapse } = useCollapseTransition({
    profileTargetRef,
    onTransitionComplete,
  });

  // Subscribe to recalibration schedule + Day-14 offer.
  useEffect(() => {
    if (!user) return;
    let active = true;
    ensureRecalibrationSchedule().catch((err) =>
      console.warn('ensureRecalibrationSchedule failed:', err),
    );
    const unsubSched = subscribeToRecalibrationSchedule(user.uid, (sched) => {
      if (active) setSchedule(sched);
    });
    const unsubOffer = subscribeToDay14Offer(user.uid, (offer) => {
      if (active) setDay14Offer(offer);
    });
    return () => {
      active = false;
      unsubSched();
      unsubOffer();
    };
  }, [user, setSchedule]);

  // Load profile / content / learning-paths and decide initial view.
  useEffect(() => {
    if (!user) return;
    const loadData = async () => {
      const [profileResult, contentResult, pathsResult] = await Promise.allSettled([
        loadProfile(user.uid),
        loadUserContent(user.uid),
        getUserLearningPaths(user.uid),
      ]);
      if (profileResult.status === 'fulfilled') setProfile(profileResult.value);
      if (contentResult.status === 'fulfilled') setContent(contentResult.value);
      if (pathsResult.status === 'fulfilled') setLearningPaths(pathsResult.value);

      const loadedProfile = profileResult.status === 'fulfilled' ? profileResult.value : null;
      const hasSeenReveal = (() => {
        try { return localStorage.getItem(REVEAL_SEEN_KEY) === 'true'; }
        catch { return false; }
      })();
      setView(loadedProfile && !hasSeenReveal ? 'reveal' : 'dashboard');
      setLoading(false);
    };
    loadData();
  }, [user]);

  // Analytics
  useEffect(() => {
    if (!loading && view === 'dashboard') trackPageView('dashboard');
  }, [loading, view]);

  const handleContentDeleted = useCallback((contentId: string) => {
    setContent((prev) => prev.filter((c) => c.contentId !== contentId));
    setLearningPaths((prev) => prev.filter((p) => p.contentId !== contentId));
  }, []);

  // Auth guard + loading spinner
  if (!user) return <Navigate to="/login" replace />;
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasProfile = !!profile;
  const tier = (user.subscription ?? 'free') as SubscriptionTier;
  const isFree = tier === 'free';
  const dimensions = hasProfile ? getDimensionsFromProfile(profile, isFree) : [];
  const summaryText = profile?.summary?.summaryText ?? '';
  const highlightDimensions = dimensions.filter((d) => d.value >= 75 && !d.locked);

  // Profile-reveal → collapsing → dashboard orchestration.
  const handleCollapseStart = (flights: CardFlightData) => {
    setView('collapsing');
    requestAnimationFrame(() => startCollapse(flights));
  };
  const handleExpand = () => setProfileExpanded(true);
  const handleCollapseOverlay = () => setProfileExpanded(false);
  const handleContinueLearning = (contentId: string) => navigate(`/learn/${contentId}`);
  const handleStartLearning = (contentId: string) => navigate(`/learn/${contentId}`);

  const showReveal = view === 'reveal' || view === 'collapsing';
  const showDashboard = view === 'collapsing' || view === 'dashboard';

  return (
    <div className="min-h-screen text-white font-body relative" style={{ background: '#0A0E1F' }}>
      {/* Ambient backdrop */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 40% at 20% 10%, rgba(108,92,231,0.10) 0%, transparent 60%),' +
            'radial-gradient(ellipse 50% 50% at 85% 85%, rgba(240,165,0,0.06) 0%, transparent 60%),' +
            'radial-gradient(ellipse 40% 30% at 50% 50%, rgba(10,191,188,0.05) 0%, transparent 70%)',
        }}
      />

      {/* First-login reveal overlay */}
      {showReveal && hasProfile && (
        <ProfileRevealScreen
          dimensions={dimensions}
          summaryText={summaryText}
          highlightDimensions={highlightDimensions}
          onCollapse={handleCollapseStart}
          particleTarget={particleTarget}
          collapsePhase={collapsePhase}
        />
      )}

      {/* Expanded profile overlay — shown when user clicks Expand on the profile card */}
      {view === 'dashboard' && profileExpanded && hasProfile && (
        <ProfileExpandedOverlay
          dimensions={dimensions}
          summaryText={summaryText}
          highlightDimensions={highlightDimensions}
          onClose={handleCollapseOverlay}
        />
      )}

      {/* Main bento layout */}
      {showDashboard && (
        <div
          className="transition-opacity duration-500 ease-out"
          style={{
            opacity: view === 'collapsing' ? 0 : 1,
            transitionDelay: '0.1s',
            pointerEvents: view === 'dashboard' ? 'auto' : 'none',
          }}
        >
          <BentoLayout
            user={user as UserProfile}
            tier={tier}
            profile={profile}
            dimensions={dimensions}
            content={content}
            learningPaths={learningPaths}
            day14Offer={day14Offer}
            recalStatus={recalStatus}
            profileTargetRef={profileTargetRef}
            onExpandProfile={handleExpand}
            onOpenFullWizard={() => setFullWizardOpen(true)}
            onOpenCheck={() => setCheckOpen(true)}
            onOpenPreview={() => setPreviewOpen(true)}
            onEnterAdaptation={() => { void enterAdaptationMode(); }}
            onContinueLearning={handleContinueLearning}
            onStartLearning={handleStartLearning}
            onContentDeleted={handleContentDeleted}
            onNavigate={navigate}
          />
        </div>
      )}

      {/* Recalibration modals */}
      <PreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} />
      <CheckWizard open={checkOpen} onClose={() => setCheckOpen(false)} />
      <FullWizard
        open={fullWizardOpen}
        softReset={recalStatus === 'reanchor'}
        onClose={() => setFullWizardOpen(false)}
      />

      {/* Professor Cogni slide-over */}
      <TutorPanel />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Bento layout
// ─────────────────────────────────────────────────────────────

interface BentoLayoutProps {
  user: UserProfile;
  tier: Tier;
  profile: CogniPrintProfile | null;
  dimensions: DimensionDisplayData[];
  content: ContentDocument[];
  learningPaths: LearningPath[];
  day14Offer: Day14Offer | null;
  recalStatus: RecalibrationStatus | null;
  profileTargetRef: React.Ref<HTMLDivElement>;
  onExpandProfile: () => void;
  onOpenFullWizard: () => void;
  onOpenCheck: () => void;
  onOpenPreview: () => void;
  onEnterAdaptation: () => void;
  onContinueLearning: (contentId: string) => void;
  onStartLearning: (contentId: string) => void;
  onContentDeleted: (contentId: string) => void;
  onNavigate: (path: string) => void;
}

function BentoLayout({
  user,
  tier,
  profile,
  dimensions,
  content,
  learningPaths,
  day14Offer,
  profileTargetRef,
  onExpandProfile,
  onOpenFullWizard,
  onOpenCheck,
  onOpenPreview,
  onEnterAdaptation,
  onContinueLearning,
  onStartLearning,
  onContentDeleted,
  onNavigate,
}: BentoLayoutProps) {
  const isFree = tier === 'free';
  const hasProfile = !!profile;
  const tierLimits = TIER_LIMITS[tier];
  const streakDays = user.streakDays ?? 0;
  const displayName = user.displayName ?? 'there';
  const greeting = greetingFor(new Date().getHours());

  // Content / path derivations
  const validContent = content.filter((c) => c.processingStatus !== 'error');
  const mostRecentPath = learningPaths.find((p) => p.generationStatus === 'complete');
  const mostRecentContentTitle = mostRecentPath
    ? content.find((c) => c.contentId === mostRecentPath.contentId)?.title ?? 'Your learning path'
    : null;
  const mostRecentEta = mostRecentPath
    ? `${Math.max(1, Math.round((mostRecentPath.nodes.length - mostRecentPath.progress.completedNodes.length) * 8))} min`
    : null;

  // Upload quick action
  const canUpload = !isFree || validContent.length < 1;
  const freeUploadUsed = isFree && validContent.length >= 1;
  const uploadsIncluded = tierLimits.uploads.included;
  const uploadSubtitle = isFree
    ? (freeUploadUsed ? 'Free upload used' : '1 free upload (10 pages)')
    : `${validContent.length} of ${uploadsIncluded}/mo`;
  const uploadProgress = isFree
    ? (freeUploadUsed ? 1 : 0)
    : validContent.length / Math.max(uploadsIncluded, 1);
  const uploadOverage = Math.max(0, validContent.length - uploadsIncluded);
  const uploadAdditionalPrice = tierLimits.uploads.additionalPrice;
  const uploadEstimatedCharge =
    uploadAdditionalPrice !== null ? uploadOverage * uploadAdditionalPrice : 0;

  // Tutor quick action
  const tutorSubtitle = isFree
    ? '3 free queries'
    : tierLimits.tutorQueries.included === Infinity
      ? 'Unlimited queries'
      : `${tierLimits.tutorQueries.included} queries/mo`;
  const tutorReadyContent = validContent
    .filter((c) => learningPaths.some((p) => p.contentId === c.contentId && p.generationStatus === 'complete'))
    .map((c) => ({ contentId: c.contentId, title: c.title }));

  // Quick-glance mastery/velocity stats (computed from learning paths)
  const { mastered, covered, velocity } = useMemo(() => {
    let masteredCount = 0;
    let coveredCount = 0;
    let weekCount = 0;
    const now = Date.now();
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    for (const path of learningPaths) {
      coveredCount += path.nodes?.length ?? 0;
      const scores = path.progress?.checkpointScores ?? {};
      for (const cp of Object.values(scores)) {
        if (cp.score >= 80) {
          masteredCount += 1;
          const ts = cp.timestamp ? Date.parse(cp.timestamp) : NaN;
          if (!Number.isNaN(ts) && now - ts <= weekMs) weekCount += 1;
        }
      }
    }
    return { mastered: masteredCount, covered: coveredCount, velocity: weekCount };
  }, [learningPaths]);

  return (
    <div className="relative max-w-[1440px] mx-auto px-6 pb-6">
      {/* Top-of-page banners — all self-hiding when the condition isn't met. */}
      <div className="pt-3 space-y-3">
        <UnacknowledgedChangesBanner />
        <ReadinessBanner
          examDate={user.examDate ?? null}
          examLabel={user.examLabel ?? null}
          paths={learningPaths}
        />
        <DueForReviewCard userId={user.uid} onNavigate={onNavigate} />
        <Day14CtaBanner
          user={user}
          offer={day14Offer}
          onStart={onOpenPreview}
          onStartCheck={onOpenCheck}
        />
        <AutoPilotCard userId={user.uid} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="grid grid-cols-1 lg:grid-cols-12 auto-rows-min gap-3 pt-3"
      >
        {/* ─── Greeting (top wide) ─── */}
        <Tile className="lg:col-span-8 p-5">
          <div className="flex items-center justify-between gap-6">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gold-500 flex items-center gap-2 mb-1">
                <span className="w-[5px] h-[5px] rounded-full bg-gold-500 animate-dashboard-pulse-gold" />
                Your command centre
              </div>
              <h1 className="font-heading font-bold text-[28px] leading-[1.08] tracking-tight m-0">
                {greeting},{' '}
                <span className="bg-gradient-to-br from-indigo-400 via-indigo-300 to-gold-400 bg-clip-text text-transparent">
                  {displayName}
                </span>
              </h1>
              <p className="text-content-secondary text-xs mt-1 max-w-lg">
                Your CogniPrint is actively shaping every path — keep the streak alive.
              </p>
            </div>
            <div className="hidden md:flex items-center gap-3 shrink-0">
              <SystemHealthBadge />
              <TierBadge tier={tier} size="lg" />
            </div>
          </div>
        </Tile>

        {/* ─── Streak ─── */}
        <Tile className="lg:col-span-4 p-5 flex flex-col justify-between">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-content-dim">
            <Flame size={12} className="text-gold-500" />
            Streak
          </div>
          <div className="flex items-end gap-3 mt-2">
            <div className="font-heading font-bold text-[46px] leading-none bg-gradient-to-br from-gold-400 to-orange-500 bg-clip-text text-transparent">
              <CountUp value={streakDays} duration={1100} />
            </div>
            <div className="pb-1.5">
              <div className="text-sm text-content font-semibold leading-tight">
                {streakDays === 1 ? 'day' : 'days'}
              </div>
              <div className="text-[10px] text-content-muted">
                {streakDays > 0 ? 'keep it going' : 'start today'}
              </div>
            </div>
          </div>
        </Tile>

        {/* ─── Profile Card (left, tall) ─── */}
        <Tile className="lg:col-span-3 lg:row-span-2 p-5 flex flex-col" innerRef={profileTargetRef}>
          <div className="flex items-start justify-between mb-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-400 flex items-center gap-1.5">
              <span className="w-[5px] h-[5px] rounded-full bg-indigo-400" />
              Learning Profile
            </div>
            {hasProfile && (
              <button
                type="button"
                onClick={onExpandProfile}
                className="text-[12px] text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-0.5 transition-colors"
                aria-label="Expand profile"
              >
                Expand ↗
              </button>
            )}
          </div>
          <h2 className="font-heading font-bold text-[17px] m-0 mb-3">Your CogniPrint</h2>
          {hasProfile ? (
            <>
              <div className="flex justify-center mb-3">
                <FingerprintRings dims={dimensions} size={130} visible />
              </div>
              <div className="space-y-2">
                {dimensions.map((dim) => (
                  <DimensionBar key={dim.key} dim={dim} />
                ))}
              </div>
              <div className="mt-auto pt-3 flex flex-col gap-2">
                {!isFree && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <RecalibrationCountdown onOpenWizard={onOpenFullWizard} compact />
                  </div>
                )}
                {!isFree && profile?.diagnosticType === 'quick' && (
                  <button
                    type="button"
                    onClick={() => onNavigate('/diagnostic?mode=full')}
                    className="text-[11px] uppercase tracking-wider font-semibold text-gold-400 hover:text-gold-300 transition-colors text-left"
                  >
                    Complete full diagnostic →
                  </button>
                )}
                <button
                  type="button"
                  onClick={onEnterAdaptation}
                  className="inline-flex items-center gap-1 text-[10px] text-content-muted hover:text-content transition-colors"
                >
                  <SlidersHorizontal size={10} />
                  Adjust your CogniPrint
                </button>
                {isFree && (
                  <Link to="/pricing" className="mt-1">
                    <button
                      type="button"
                      className="w-full relative flex items-center justify-center text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-2.5 rounded-full
                                 bg-gradient-to-r from-gold-500/18 to-gold-500/6
                                 border border-gold-500/40
                                 text-gold-400
                                 hover:from-gold-500/28 hover:to-gold-500/12
                                 hover:border-gold-500/55
                                 transition-colors
                                 shadow-[0_2px_10px_-3px_rgba(240,165,0,0.3)]"
                    >
                      <Lock size={11} className="absolute left-4" />
                      <span>Upgrade to unlock</span>
                    </button>
                  </Link>
                )}
              </div>
            </>
          ) : (
            <EmptyProfilePrompt onStart={() => onNavigate('/diagnostic?mode=quick')} />
          )}
        </Tile>

        {/* ─── Middle stack: top tile + floating orb + bottom tile ─── */}
        <MiddleOrbStack
          user={user}
          profile={profile}
          dimensions={dimensions}
          onExploreProfile={onExpandProfile}
        />

        {/* ─── Right column: Upload + Professor Cogni stacked, matches Profile/Middle height ─── */}
        <div className="lg:col-span-3 lg:row-span-2 grid grid-rows-2 gap-3" style={{ minHeight: 600 }}>
          {/* Upload quick action */}
          <Tile
            className={`min-h-0 p-4 transition-colors relative overflow-hidden flex flex-col ${canUpload ? '' : 'opacity-60'}`}
          >
            <div
              className="absolute pointer-events-none"
              style={{
                top: -20,
                right: -20,
                width: 120,
                height: 120,
                background: 'radial-gradient(circle, rgba(108,92,231,0.18) 0%, transparent 65%)',
              }}
            />
            <div className="relative flex flex-col h-full">
              <div className="w-10 h-10 rounded-xl bg-indigo-500/15 flex items-center justify-center mb-3">
                {canUpload ? <Upload size={16} className="text-indigo-400" /> : <Lock size={16} className="text-content-dim" />}
              </div>
              <div className="font-heading text-[15px] font-semibold">Upload Content</div>
              <div className="text-xs text-content-muted mt-0.5">{uploadSubtitle}</div>
              {uploadAdditionalPrice !== null && (
                <div className="text-[10px] text-content-dim font-body mt-0.5">
                  ${uploadAdditionalPrice.toFixed(2)} / upload
                  {uploadEstimatedCharge > 0 && (
                    <span style={{ color: '#F59E0B' }}> · ${uploadEstimatedCharge.toFixed(2)} overage</span>
                  )}
                </div>
              )}
              <div className="mt-3 h-[3px] rounded-sm bg-dark-subtle overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-400 to-indigo-600" style={{ width: `${Math.min(100, uploadProgress * 100)}%` }} />
              </div>
              <button
                type="button"
                onClick={canUpload ? () => onNavigate('/upload') : () => onNavigate('/pricing')}
                className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-body font-semibold transition-colors cursor-pointer
                  ${canUpload
                    ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white hover:from-indigo-400 hover:to-indigo-500 shadow-[0_2px_10px_-3px_rgba(108,92,231,0.45)]'
                    : 'bg-gold-500/15 border border-gold-500/40 text-gold-400 hover:bg-gold-500/25'}`}
              >
                {canUpload ? (
                  <>
                    <Upload size={14} />
                    Upload
                  </>
                ) : (
                  <>
                    <Lock size={14} />
                    Upgrade to upload
                  </>
                )}
              </button>
            </div>
          </Tile>

          {/* Professor Cogni */}
          <div className="min-h-0 flex">
            <TutorEntryTile
              eligibleContent={tutorReadyContent}
              tutorSubtitle={tutorSubtitle}
              onNavigate={onNavigate}
            />
          </div>
        </div>

        {/* ─── Mini stats row (left, below profile card) ─── */}
        <StatTile
          icon={<Award size={14} />}
          label="Mastered"
          value={mastered}
          color="#F0A500"
          sub={covered > 0 ? `of ${covered} concepts` : 'no concepts yet'}
          className="lg:col-span-3"
        />

        {/* ─── Continue Learning (mid row, spans 6) ─── */}
        <div className="lg:col-span-6">
          {mostRecentPath && mostRecentContentTitle ? (
            <ContinueLearningBanner
              title={mostRecentContentTitle}
              completed={mostRecentPath.progress.completedNodes.length}
              total={mostRecentPath.nodes.length}
              estimatedTimeLeft={mostRecentEta ?? ''}
              onContinue={() => onContinueLearning(mostRecentPath.contentId)}
            />
          ) : (
            <Tile className="p-4 h-full flex items-center justify-center text-center">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-content-dim mb-1">Continue learning</div>
                <div className="text-sm text-content-muted mb-3">
                  {canUpload ? 'Upload content to start a learning path.' : 'Free upload already used — upgrade for more.'}
                </div>
                <Button
                  size="sm"
                  variant={canUpload ? 'primary' : 'gold'}
                  onClick={() => onNavigate(canUpload ? '/upload' : '/pricing')}
                >
                  {canUpload ? 'Upload Content' : 'Upgrade'}
                </Button>
              </div>
            </Tile>
          )}
        </div>

        {/* ─── Velocity stat (right, under continue) ─── */}
        <StatTile
          icon={<Zap size={14} />}
          label="Velocity"
          value={velocity}
          color="#55EFC4"
          sub={velocity === 1 ? 'mastered this week' : 'mastered this week'}
          className="lg:col-span-3"
        />

        {/* ─── Your Journey (bottom wide, spans 8) ─── */}
        <div className="lg:col-span-8">
          <YourJourney
            paths={learningPaths}
            content={content}
            userSince={user.createdAt ?? null}
          />
        </div>

        {/* ─── Mastery Dashboard (bottom right, spans 4) ─── */}
        <div className="lg:col-span-4">
          <MasteryDashboard
            tier={tier}
            paths={learningPaths}
            content={content}
            profile={profile}
          />
        </div>

        {/* ─── Learning Paths List — full width below; empty state shown when none ─── */}
        <div className="lg:col-span-12">
          <LearningPathsList
            paths={learningPaths}
            content={content}
            onContinue={onContinueLearning}
            onStart={onStartLearning}
            onContentDeleted={onContentDeleted}
          />
        </div>
      </motion.div>
    </div>
  );
}

// Small inline component: no-profile prompt rendered inside the Profile tile.
function EmptyProfilePrompt({ onStart }: { onStart: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center py-6">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center mb-4">
        <Fingerprint size={26} className="text-white" />
      </div>
      <p className="text-xs text-content-secondary font-body mb-4 max-w-[220px]">
        Take the quick CogniPrint diagnostic to map your learning profile.
      </p>
      <Button size="sm" variant="gold" onClick={onStart}>
        Start Diagnostic
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MiddleOrbStack — two tiles with circular notches + floating orb
// (NotchedTile primitive lives in ../components/dashboard/NotchedTile.tsx)
// ─────────────────────────────────────────────────────────────

function MiddleOrbStack({
  user,
  profile,
  dimensions,
  onExploreProfile,
}: {
  user: UserProfile;
  profile: CogniPrintProfile | null;
  dimensions: DimensionDisplayData[];
  onExploreProfile: () => void;
}) {
  // Only dimensions that were actually assessed count towards the headline
  // figure — a dimension the diagnostic never captured (motivation, on a
  // profile that pre-dates the Full Diagnostic) is not a revealed dimension.
  const assessedDimensionCount = dimensions.filter((d) => !d.locked && d.confidence > 0).length;
  // Mouse tracking for cursor-aware orb interactions.
  const orbRef = useRef<HTMLDivElement>(null);
  const [cursorBias, setCursorBias] = useState<{ x: number; y: number } | null>(null);
  const [clickAction, setClickAction] = useState<{ key: number; count: number } | null>(null);

  // Rotating-sayings state — customs live in Firestore at
  // users/{uid}/customisations/customSayings and sync live across sessions.
  const [sayingIdx, setSayingIdx] = useState(0);
  const [customs, setCustoms] = useState<string[]>(Array(10).fill(''));
  const [customiseOpen, setCustomiseOpen] = useState(false);

  // Initial fetch + live subscription.
  useEffect(() => {
    let active = true;
    loadCustomSayings(user.uid).then((initial) => {
      if (active) setCustoms(initial);
    });
    const unsub = subscribeToCustomSayings(user.uid, (next) => {
      if (active) setCustoms(next);
    });
    return () => {
      active = false;
      unsub();
    };
  }, [user.uid]);

  // Cycle sayings every 15 seconds.
  useEffect(() => {
    const id = window.setInterval(() => {
      setSayingIdx((i) => (i + 1) % 10);
    }, 15000);
    return () => window.clearInterval(id);
  }, []);

  const currentSaying: Saying = (() => {
    const custom = customs[sayingIdx]?.trim();
    if (custom) return { text: custom, highlight: autoHighlight(custom) };
    return DEFAULT_SAYINGS[sayingIdx];
  })();

  // Multi-click detection: count clicks within a 300ms window, then dispatch
  // a single click-burst action with that count. 1 = implosion, 2..5 TBD.
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<number | null>(null);
  const clickKeyRef = useRef(0);
  const triggerClick = () => {
    clickCountRef.current += 1;
    if (clickTimerRef.current !== null) window.clearTimeout(clickTimerRef.current);
    clickTimerRef.current = window.setTimeout(() => {
      const count = Math.min(5, clickCountRef.current);
      clickKeyRef.current += 1;
      setClickAction({ key: clickKeyRef.current, count });
      clickCountRef.current = 0;
      clickTimerRef.current = null;
    }, 300);
  };

  // Smoothed motion values that drive the orb's cursor-tilt.
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 55, damping: 18, mass: 0.8 });
  const springY = useSpring(mouseY, { stiffness: 55, damping: 18, mass: 0.8 });
  const cursorTiltY = useTransform(springX, [-2, 2], [-14, 14]);
  const cursorTiltX = useTransform(springY, [-2, 2], [10, -10]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const el = orbRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const biasX = (e.clientX - centerX) / (rect.width / 2);
      const biasY = (e.clientY - centerY) / (rect.height / 2);
      setCursorBias({ x: biasX, y: biasY });
      mouseX.set(biasX);
      mouseY.set(biasY);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [mouseX, mouseY]);

  return (
    <div
      className="lg:col-span-6 lg:row-span-2 relative flex flex-col gap-3"
      style={{ minHeight: 600 }}
    >
      {/* Top tile — concave bottom edge, rounded on ALL four outer corners,
         and a thin light stroke tracing the entire outline including the notch. */}
      <NotchedTile position="top" contentClassName="px-8 pt-5 pb-0">
        <div className="flex flex-col items-center">
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-500 flex items-center gap-2 mb-2">
            <span className="w-[5px] h-[5px] rounded-full bg-gold-500 animate-dashboard-pulse-gold" />
            Your Cognitive Fingerprint
          </div>
          {/* Crossfade container — fixed height so layout stays stable while
             the old saying fades out and the new one fades in on top. */}
          <div className="relative w-full" style={{ height: 44 }}>
            <AnimatePresence>
              <motion.h2
                key={sayingIdx}
                className="absolute inset-0 flex items-center justify-center font-heading font-bold text-[28px] leading-[1.05] tracking-tight m-0 text-center"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              >
                <HighlightedSaying
                  text={currentSaying.text}
                  highlight={currentSaying.highlight}
                />
              </motion.h2>
            </AnimatePresence>
          </div>
          <button
            type="button"
            onClick={() => setCustomiseOpen(true)}
            className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-content-muted hover:text-content transition-colors"
          >
            <Pencil size={10} />
            Customise
          </button>
        </div>
      </NotchedTile>

      {/* Bottom tile — concave top edge, same full outline-trace stroke. */}
      <NotchedTile position="bottom" contentClassName="px-8 pt-0 pb-6 flex flex-col justify-end h-full">
        <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] text-content-muted mb-4">
          <span className="w-[5px] h-[5px] rounded-full bg-[#55EFC4] animate-pulse" />
          <span>Actively recalibrating</span>
        </div>
        <div className="flex items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <ProfileStat value={`v${profile?.profileVersion ?? 1}`} label="version" />
            <StatDivider />
            <ProfileStat
              value={`${Math.round((profile?.overallConfidence ?? 0) * 100)}%`}
              label="confidence"
              accent="#55EFC4"
            />
            <StatDivider />
            <ProfileStat value={String(assessedDimensionCount)} label="dimensions" accent="#F0A500" />
            <StatDivider />
            <ProfileStat value={relativeAge(profile?.lastUpdated)} label="ago" />
          </div>
          <button
            type="button"
            onClick={onExploreProfile}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 hover:from-indigo-400 hover:to-indigo-500 transition-all shadow-lg shadow-indigo-500/25 shrink-0"
          >
            Explore profile <ArrowRight size={14} />
          </button>
        </div>
      </NotchedTile>

      {/* Floating orb — centred across the seam. Outer div sets perspective &
         hosts the cursor-tilt; inner div runs the continuous breathing spin.
         Click anywhere inside to fire a shockwave through the rings. */}
      <div
        ref={orbRef}
        className="absolute left-1/2 top-1/2 cursor-pointer select-none"
        style={{
          transform: 'translate(-50%, -50%)',
          width: ORB_RADIUS * 2,
          height: ORB_RADIUS * 2,
          perspective: '900px',
        }}
        onClick={triggerClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            triggerClick();
          }
        }}
      >
        {/* Cursor-tilt layer — leans the orb toward the mouse pointer. */}
        <motion.div
          className="absolute inset-0"
          style={{
            transformStyle: 'preserve-3d',
            rotateX: cursorTiltX,
            rotateY: cursorTiltY,
          }}
        >
          {/* Breathing/spin layer — continuous oscillation independent of cursor. */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            style={{ transformStyle: 'preserve-3d' }}
            animate={{
              rotateZ: 360,
              rotateX: [0, 10, -8, 6, 0],
              rotateY: [0, -14, 10, -6, 0],
            }}
            transition={{
              rotateZ: { duration: 18, repeat: Infinity, ease: 'linear' },
              rotateX: { duration: 6.5, repeat: Infinity, ease: 'easeInOut' },
              rotateY: { duration: 9, repeat: Infinity, ease: 'easeInOut' },
            }}
          >
            <ParticleFingerprint
              active
              alive
              cursorBias={cursorBias}
              clickAction={clickAction}
              size={ORB_RADIUS * 2}
              canvasSize={600}
            />
          </motion.div>
        </motion.div>
      </div>

      <CustomiseSayingsModal
        open={customiseOpen}
        customs={customs}
        onSave={(next) => {
          setCustoms(next);
          saveCustomSayings(user.uid, next).catch((err) =>
            console.warn('saveCustomSayings failed:', err),
          );
        }}
        onClose={() => setCustomiseOpen(false)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Rotating tagline system
// ─────────────────────────────────────────────────────────────

type Saying = { text: string; highlight: string };

// 10 tongue-in-cheek defaults. Each has a pre-picked "highlight word"
// that gets the indigo→gold gradient treatment.
const DEFAULT_SAYINGS: Saying[] = [
  { text: "No more sloggin' for your noggin'", highlight: 'your' },
  { text: 'Because your cerebrum deserves better', highlight: 'cerebrum' },
  { text: 'Redesigning Study Matter. For Your Gray Matter', highlight: 'Gray Matter' },
  { text: 'Intellect, Personified', highlight: 'Intellect' },
  { text: 'Strudel for your noodle', highlight: 'noodle' },
  { text: 'Cookies for your cortex', highlight: 'cortex' },
  { text: 'Where your synapses sing', highlight: 'synapses' },
  { text: 'Fuel for the fire between your ears', highlight: 'fire' },
  { text: 'Upgrades for your upstairs', highlight: 'upstairs' },
  { text: 'Brain gains, zero pains', highlight: 'Brain' },
];

const BRAIN_SYNONYMS = [
  'gray matter', 'grey matter',
  'cerebrum', 'cortex', 'cerebellum', 'cranium',
  'neurons', 'neuron', 'synapses', 'synapse',
  'noggin', 'noodle', 'upstairs', 'skull', 'brain',
  'mind', 'intellect',
];

const STOP_WORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'but', 'for', 'of', 'to', 'in', 'on', 'at',
  'by', 'with', 'from', 'as', 'is', 'are', 'was', 'were', 'be', 'been',
  'more', 'less', 'no', 'yes', 'so', 'than', 'then', 'your', 'you', 'my',
  'me', 'it', 'its', 'that', 'this', 'these', 'those',
]);

function autoHighlight(text: string): string {
  // 1) Prefer "you" / "your"
  const youMatch = text.match(/\b(your|you)\b/i);
  if (youMatch) return youMatch[0];
  // 2) Prefer brain synonym (case-insensitive substring, longest first)
  const lower = text.toLowerCase();
  const synonyms = [...BRAIN_SYNONYMS].sort((a, b) => b.length - a.length);
  for (const syn of synonyms) {
    const idx = lower.indexOf(syn);
    if (idx >= 0) return text.substring(idx, idx + syn.length);
  }
  // 3) Fallback: longest non-stopword token
  const tokens = text.split(/\s+/).filter((w) => w.length >= 4);
  const candidates = tokens.filter((w) => !STOP_WORDS.has(w.toLowerCase().replace(/[^a-z]/g, '')));
  if (candidates.length === 0) return tokens[0] ?? text;
  return candidates.sort((a, b) => b.length - a.length)[0];
}

const SAYING_MAX_WORDS = 8;
const SAYING_MAX_CHARS = 60;

function HighlightedSaying({ text, highlight }: { text: string; highlight: string }) {
  // Outer <span> collapses the before/match/after parts into a single inline
  // run. Without it, the h2's flex layout treats each part as a separate
  // flex item and eats the whitespace between them.
  if (!highlight) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(highlight.toLowerCase());
  if (idx < 0) return <span>{text}</span>;
  return (
    <span>
      {text.substring(0, idx)}
      <span className="bg-gradient-to-br from-indigo-300 to-gold-400 bg-clip-text text-transparent">
        {text.substring(idx, idx + highlight.length)}
      </span>
      {text.substring(idx + highlight.length)}
    </span>
  );
}

 
function CustomiseSayingsModal({
  open,
  customs,
  onSave,
  onClose,
}: {
  open: boolean;
  customs: string[];
  onSave: (next: string[]) => void;
  onClose: () => void;
}) {
  const [drafts, setDrafts] = useState<string[]>(customs);

  // Keep local drafts in sync when the modal re-opens with fresh customs.
  useEffect(() => {
    if (open) setDrafts(customs);
  }, [open, customs]);

  if (!open) return null;

  // Hard-prevent: reject any edit that would exceed the word or char cap.
  const updateDraft = (i: number, value: string) => {
    if (value.length > SAYING_MAX_CHARS) return;
    const words = value.trim().split(/\s+/).filter(Boolean);
    if (words.length > SAYING_MAX_WORDS) return;
    setDrafts((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  };

  const handleSave = () => {
    onSave(drafts.map((d) => d.trim()));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(4, 7, 17, 0.72)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-dark-border overflow-hidden"
        style={{
          background:
            'linear-gradient(160deg, rgba(28,34,60,0.98) 0%, rgba(20,25,48,0.97) 40%, rgba(14,18,36,0.97) 80%, rgba(9,12,26,0.98) 100%)',
          boxShadow: [
            '0 28px 60px -18px rgba(0, 0, 0, 0.75)',
            '0 10px 22px -6px rgba(0, 0, 0, 0.55)',
            'inset 0 1px 0 rgba(255, 255, 255, 0.07)',
            'inset 0 -1px 0 rgba(0, 0, 0, 0.55)',
          ].join(', '),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-4 border-b border-dark-border">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gold-500 mb-1">
              Make it yours
            </div>
            <h3 className="font-heading font-bold text-[20px] m-0">Customise your sayings</h3>
            <p className="text-xs text-content-muted mt-1">
              Write up to 10 of your own. Max {SAYING_MAX_WORDS} words each. Blank slots fall back to the defaults.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-content-dim hover:text-content transition-colors shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-2 max-h-[60vh] overflow-y-auto">
          {drafts.map((draft, i) => {
            const words = draft.trim().split(/\s+/).filter(Boolean).length;
            return (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-content-dim w-5 shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <input
                  type="text"
                  className="flex-1 bg-dark-subtle border border-dark-border rounded-lg px-3 py-2 text-sm text-content font-body placeholder:text-content-dim focus:border-indigo-500/50 focus:outline-none transition-colors"
                  placeholder={DEFAULT_SAYINGS[i].text}
                  value={draft}
                  onChange={(e) => updateDraft(i, e.target.value)}
                  maxLength={SAYING_MAX_CHARS}
                />
                <span className="text-[10px] font-mono text-content-dim w-8 text-right shrink-0">
                  {words}/{SAYING_MAX_WORDS}
                </span>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-4 border-t border-dark-border flex items-center justify-between gap-2">
          <span className="text-[11px] text-content-muted">
            Saved to your profile · applied instantly
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-content-muted hover:text-content px-4 py-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="text-sm font-semibold px-4 py-2 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 hover:from-indigo-400 hover:to-indigo-500 transition-colors shadow-lg shadow-indigo-500/25"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TierBadge — distinctive per-plan pill
// ─────────────────────────────────────────────────────────────

// Tier aligns with SubscriptionTier from shared. Free is omitted from the
// badge config below because free users don't get a badge in the greeting.
type Tier = SubscriptionTier;
type PaidTier = Exclude<Tier, 'free'>;

const TIER_CONFIG: Record<
  PaidTier,
  { label: string; icon: React.ComponentType<{ size?: number; className?: string }>; accent: string; accentRgb: string }
> = {
  learner: {
    label: 'Learner',
    icon: Sparkles,
    accent: '#6C5CE7',
    accentRgb: '108,92,231',
  },
  pro: {
    label: 'Pro',
    icon: Crown,
    accent: '#F0A500',
    accentRgb: '240,165,0',
  },
  teams: {
    label: 'Teams',
    icon: Users,
    accent: '#0ABFBC',
    accentRgb: '10,191,188',
  },
};

function TierBadge({ tier, size = 'md' }: { tier: Tier; size?: 'sm' | 'md' | 'lg' }) {
  // Free tier gets no badge — the "Upgrade to unlock" affordance on the
  // profile card carries the plan messaging for free users.
  if (tier === 'free') return null;
  const cfg = TIER_CONFIG[tier];
  const Icon = cfg.icon;
  const padding =
    size === 'sm' ? 'px-2.5 py-1'
    : size === 'lg' ? 'px-4 py-2'
    : 'px-3 py-1.5';
  const text =
    size === 'sm' ? 'text-[10px]'
    : size === 'lg' ? 'text-[13px]'
    : 'text-[11px]';
  const iconSize = size === 'sm' ? 10 : size === 'lg' ? 14 : 12;
  const gap = size === 'lg' ? 'gap-2' : 'gap-1.5';
  return (
    <div
      className={`inline-flex items-center rounded-full border font-bold uppercase tracking-[0.14em] ${gap} ${padding} ${text}`}
      style={{
        background: `linear-gradient(135deg, rgba(${cfg.accentRgb}, 0.24), rgba(${cfg.accentRgb}, 0.08))`,
        borderColor: `rgba(${cfg.accentRgb}, 0.42)`,
        color: cfg.accent,
        boxShadow: [
          `inset 0 1px 0 rgba(255,255,255,0.08)`,
          `0 2px 8px -2px rgba(${cfg.accentRgb}, 0.35)`,
        ].join(', '),
      }}
    >
      <Icon size={iconSize} className="shrink-0" />
      <span>{cfg.label}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Bottom-tile stat cluster primitives
// ─────────────────────────────────────────────────────────────

function ProfileStat({
  value, label, accent,
}: { value: string; label: string; accent?: string }) {
  return (
    <div className="flex flex-col leading-none">
      <div
        className="font-heading font-bold text-[15px]"
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-content-muted mt-1">
        {label}
      </div>
    </div>
  );
}

function StatDivider() {
  return <div className="w-px h-7 bg-dark-border" />;
}

// ─────────────────────────────────────────────────────────────
// Primitive: Tile (bento card)
// ─────────────────────────────────────────────────────────────

interface TileProps {
  children: React.ReactNode;
  className?: string;
  as?: 'div' | 'button';
  style?: React.CSSProperties;
  innerRef?: React.Ref<HTMLDivElement>;
  onClick?: () => void;
  disabled?: boolean;
}
function Tile({ children, className = '', as = 'div', style, innerRef, onClick, disabled }: TileProps) {
  const Tag = as;
  return (
    <Tag
      ref={innerRef as React.Ref<HTMLDivElement & HTMLButtonElement>}
      onClick={onClick}
      disabled={as === 'button' ? disabled : undefined}
      className={`${THICK_TILE_CLASSNAME} ${className}`}
      style={{ ...THICK_TILE_STYLE, ...style }}
    >
      {children}
    </Tag>
  );
}

// ─────────────────────────────────────────────────────────────
// Primitive: StatTile (compact stat card)
// ─────────────────────────────────────────────────────────────

function StatTile({
  icon, label, value, color, sub, className = '',
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  color: string;
  sub?: string;
  className?: string;
}) {
  return (
    <Tile className={`p-4 flex flex-col justify-between ${className}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-content-dim" style={{ color }}>
        {icon} <span className="text-content-dim">{label}</span>
      </div>
      <div className="mt-2">
        <div className="font-heading font-bold text-[32px] leading-none text-white">
          {typeof value === 'number' ? <CountUp value={value} /> : value}
        </div>
        {sub && <div className="text-[11px] text-content-muted mt-1">{sub}</div>}
      </div>
    </Tile>
  );
}

// ─────────────────────────────────────────────────────────────
// CountUp — animates a number up from 0 → target on mount
// ─────────────────────────────────────────────────────────────

function CountUp({
  value,
  duration = 900,
  delay = 0,
}: {
  value: number;
  duration?: number;
  delay?: number;
}) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(value) || value <= 0) {
      setDisplay(value);
      return;
    }
    let raf: number;
    let cancelled = false;
    const startTimer = window.setTimeout(() => {
      const start = performance.now();
      const tick = (now: number) => {
        if (cancelled) return;
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
        setDisplay(Math.round(eased * value));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);
    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      if (raf) cancelAnimationFrame(raf);
    };
    // Re-run when the target value changes (e.g. data refresh).
  }, [value, duration, delay]);
  return <>{display}</>;
}

// ─────────────────────────────────────────────────────────────
// DimensionBar (for the profile tile)
// ─────────────────────────────────────────────────────────────

function DimensionBar({ dim }: { dim: DimensionDisplayData }) {
  if (dim.locked) {
    return (
      <div className="flex items-center gap-2 opacity-55">
        <div
          className="w-[6px] h-[6px] rounded-full shrink-0"
          style={{ background: 'rgba(192,189,212,0.3)' }}
        />
        <div className="flex-1">
          <div className="flex justify-between items-center">
            <span className="text-[13px] text-content-dim">{dim.name}</span>
            <Lock size={10} className="text-content-dim shrink-0" />
          </div>
          <div className="h-[2.5px] mt-1 rounded-sm bg-dark-subtle overflow-hidden" />
        </div>
      </div>
    );
  }
  // A dimension with no evidence behind it gets no bar and no colour: a filled bar
  // is a claim, and there is nothing here to claim. It used to render "Low".
  if (!dim.assessed) {
    return (
      <div className="flex items-center gap-2 opacity-70">
        <div
          className="w-[6px] h-[6px] rounded-full shrink-0 border"
          style={{ borderColor: `${dim.color}80`, background: 'transparent' }}
        />
        <div className="flex-1">
          <div className="flex justify-between">
            <span className="text-[13px] text-content-muted">{dim.name}</span>
            <span className="text-[12px] text-content-dim italic">Not assessed</span>
          </div>
          <div className="h-[2.5px] mt-1 rounded-sm bg-dark-subtle overflow-hidden" />
        </div>
      </div>
    );
  }

  const isClear = dim.clarity === 'clear';
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-[6px] h-[6px] rounded-full shrink-0"
        style={{ background: dim.color, boxShadow: isClear ? `0 0 6px ${dim.color}60` : 'none' }}
      />
      <div className="flex-1">
        <div className="flex justify-between">
          <span className="text-[13px] text-content-muted">{dim.name}</span>
          <span className="text-[12px] font-semibold" style={{ color: isClear ? '#F0A500' : '#c0bdd4' }}>
            {dim.label}
          </span>
        </div>
        <div className="mt-1">
          <DimensionMeter dim={dim} size="compact" animate={false} />
        </div>
      </div>
    </div>
  );
}

